import { useState, FC, useRef, useEffect } from 'react'
import { useQuery } from '@apollo/client'
import { useDebounce } from 'use-debounce'

import { useGeolocation, useOnClickOutside, useQueryParams } from '~hooks'
import { Spinner, Grid, Button } from '~ui'
import GlowHeading, { GlowHeadingProps } from '~ui/GlowHeading'
import { Dropdown } from '~shared'
import { GetCitiesQuery, GetCitiesQueryVariables } from 'gql'
import { GET_CITIES } from 'gql/queries'

import styles from './UserLocationDropdown.module.scss'

type UserLocationDropdownProps = {
  locationTitleOptions?: Omit<GlowHeadingProps, 'children' | 'Tag'>
}

const UserLocationDropdown: FC<UserLocationDropdownProps> = ({
  locationTitleOptions = {
    size: 'h2',
  },
}) => {
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [debouncedInputValue] = useDebounce(inputValue, 200)
  const { query, replace } = useQueryParams()

  const ref = useRef(null)

  const { location, loading, setLocation, browserCoordinates } = useGeolocation()

  const variables = {
    first: 10,
    coordinatesSort: browserCoordinates
      ? `${browserCoordinates.latitude},${browserCoordinates.longitude}`
      : null,
  }

  const { data, refetch } = useQuery<GetCitiesQuery, GetCitiesQueryVariables>(GET_CITIES, {
    variables,
    skip: !open,
  })

  const options = data?.allCities?.edges
    ? data?.allCities?.edges.map(({ node: { stateCode, name, id } }) => ({
        label: `${name}, ${stateCode}`,
        value: id,
      }))
    : []

  const onSelect = (v: string) => {
    const [filteredLocation] = data.allCities?.edges.filter(({ node: { id } }) => id === v)
    replace({ query: { ...query, cityId: filteredLocation.node.id } }, null, { shallow: true })
    setLocation(filteredLocation.node)
    setOpen(false)
  }

  const onFilterInputChange = (v: string) => setInputValue(v)

  const onSelectLocation = () => setOpen((prev) => !prev)

  useOnClickOutside(ref, () => setOpen(false))

  useEffect(() => {
    if (debouncedInputValue) {
      refetch({
        ...variables,
        name: debouncedInputValue,
      })
    }
  }, [debouncedInputValue])

  if (loading) {
    return <Spinner />
  }

  return (
    <div ref={ref}>
      <Grid className={styles.location}>
        <Button icon="left-arrow" onClick={onSelectLocation}>
          {location && (
            <GlowHeading {...{ ...locationTitleOptions, Tag: 'span' }}>
              {`${location.name}, ${location.stateCode}`}
            </GlowHeading>
          )}
          {!location && 'Select location'}
        </Button>
        <div className={styles['location-dropdown']}>
          {open && (
            <Dropdown
              options={options}
              onSelect={onSelect}
              withFilter
              withOuterFilter
              onFilterInputChange={onFilterInputChange}
              placeholder="Search by city"
              withMobileModal={false}
            />
          )}
        </div>
      </Grid>
    </div>
  )
}

export default UserLocationDropdown
